/**
 * instacalc-bridge.js - Template Bridge
 *
 * Auto-wires data-ic-* attributes to the Instacalc data layer.
 * This enables AI-generated HTML prototypes to work with real calc data
 * without any React/Alpine knowledge.
 *
 * Data Attributes:
 *
 * CONTAINERS:
 *   data-ic-calc-list      - Container for calc list items
 *   data-ic-calc-editor    - The active calc editor area
 *   data-ic-rows           - Container for calc rows
 *   data-ic-share-menu     - Share controls area
 *
 * TEMPLATES (use <template> tag):
 *   data-ic-calc-item      - Template for each calc in list
 *   data-ic-row            - Template for each row in calc
 *
 * DATA BINDINGS:
 *   data-ic-title          - Shows calc title
 *   data-ic-row-input      - Input for row content
 *   data-ic-row-result     - Shows computed result
 *   data-ic-share-badge    - Shows current share status
 *   data-ic-calc-count     - Shows total calc count
 *
 * ACTIONS (on buttons):
 *   data-ic-action="new"           - Create new calc
 *   data-ic-action="open"          - Open this calc (needs data-id on parent)
 *   data-ic-action="delete"        - Delete this calc
 *   data-ic-action="add-row"       - Add new row to current calc
 *   data-ic-action="delete-row"    - Delete this row (needs data-row-index)
 *   data-ic-action="share:public"  - Share as public
 *   data-ic-action="share:private" - Share as private
 *   data-ic-action="share:wiki"    - Share as wiki
 *   data-ic-action="copy-link"     - Copy share link
 *
 * Example:
 *   <div data-ic-calc-list>
 *     <template data-ic-calc-item>
 *       <div class="calc-card" data-ic-action="open">
 *         <span data-ic-title></span>
 *         <button data-ic-action="delete">×</button>
 *       </div>
 *     </template>
 *   </div>
 */

(function() {
  'use strict';

  // Debounce helper
  function debounce(fn, ms) {
    let timeout;
    return function(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  // Escape HTML for safe rendering
  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }

  class InstacalcBridge {
    constructor() {
      this.initialized = false;
      this.boundElements = new WeakSet();
    }

    init() {
      if (this.initialized) return;

      // Wait for Instacalc data layer
      if (!window.Instacalc) {
        setTimeout(() => this.init(), 100);
        return;
      }

      this.bindAll();
      this.setupListeners();
      this.initialized = true;
      if (window.Debug) window.Debug.log('ui', 'instacalc-bridge initialized');
    }

    bindAll() {
      this.bindCalcList();
      this.bindCalcEditor();
      this.bindShareMenu();
      this.bindActions();
      this.bindDataBindings();
    }

    setupListeners() {
      const IC = window.Instacalc;

      // Re-render calc list on changes
      IC.on('calcs:changed', () => this.bindCalcList());
      IC.on('calc:selected', () => {
        this.bindCalcList();
        this.bindCalcEditor();
        this.bindShareMenu();
        this.bindDataBindings();
      });

      // Re-render rows on calc changes
      IC.on('calc:changed', debounce(() => {
        this.bindCalcEditor();
        this.bindDataBindings();
      }, 50));

      // Update share badge on share changes
      IC.on('share:changed', () => this.bindShareMenu());

      // NOTE: Removed MutationObserver that was watching document.body
      // It was a performance issue and the bridge attributes (data-ic-action, etc.)
      // aren't used in production. Event-driven updates above are sufficient.
    }

    // ===== CALC LIST =====

    bindCalcList() {
      const IC = window.Instacalc;
      const listEl = document.querySelector('[data-ic-calc-list]');
      if (!listEl) return;

      const template = listEl.querySelector('template[data-ic-calc-item]');
      if (!template) return;

      // Get all calcs
      const calcs = IC.calcs;
      const selectedId = IC.selectedCalcId;

      // Clear existing items (but keep template)
      Array.from(listEl.children).forEach(child => {
        if (child.tagName !== 'TEMPLATE') {
          child.remove();
        }
      });

      // Render each calc
      calcs.forEach(calc => {
        const clone = template.content.cloneNode(true);
        const wrapper = clone.firstElementChild || clone.children[0];

        if (!wrapper) return;

        // Set data-id for actions
        wrapper.setAttribute('data-id', calc.id);

        // Mark selected
        if (calc.id === selectedId) {
          wrapper.classList.add('ic-selected');
          wrapper.setAttribute('data-selected', 'true');
        }

        // Bind title
        const titleEl = wrapper.querySelector('[data-ic-title]');
        if (titleEl) {
          titleEl.textContent = calc.name || 'Untitled';
        }

        // Bind created date
        const createdEl = wrapper.querySelector('[data-ic-created]');
        if (createdEl && calc.created) {
          createdEl.textContent = new Date(calc.created).toLocaleDateString();
        }

        // Bind updated date
        const updatedEl = wrapper.querySelector('[data-ic-updated]');
        if (updatedEl && calc.updated) {
          updatedEl.textContent = new Date(calc.updated).toLocaleDateString();
        }

        listEl.appendChild(clone);
      });

      // Bind actions on new elements
      this.bindActions(listEl);
    }

    // ===== CALC EDITOR (ROWS) =====

    bindCalcEditor() {
      const IC = window.Instacalc;
      const editorEl = document.querySelector('[data-ic-calc-editor]');
      if (!editorEl) return;

      const rowsContainer = editorEl.querySelector('[data-ic-rows]');
      if (!rowsContainer) return;

      const template = rowsContainer.querySelector('template[data-ic-row]');
      if (!template) return;

      const rows = IC.rows;

      // Clear existing rows (but keep template)
      Array.from(rowsContainer.children).forEach(child => {
        if (child.tagName !== 'TEMPLATE') {
          child.remove();
        }
      });

      // Render each row
      rows.forEach((row, index) => {
        const clone = template.content.cloneNode(true);
        const wrapper = clone.firstElementChild || clone.children[0];

        if (!wrapper) return;

        wrapper.setAttribute('data-row-index', index);

        // Bind input
        const inputEl = wrapper.querySelector('[data-ic-row-input]');
        if (inputEl) {
          inputEl.value = row.text;

          // Only bind once
          if (!this.boundElements.has(inputEl)) {
            this.boundElements.add(inputEl);
            inputEl.addEventListener('input', debounce((e) => {
              const idx = parseInt(wrapper.getAttribute('data-row-index'), 10);
              IC.updateRow(idx, e.target.value);
            }, 150));
          }
        }

        // Bind result
        const resultEl = wrapper.querySelector('[data-ic-row-result]');
        if (resultEl) {
          if (row.error) {
            resultEl.textContent = 'Error';
            resultEl.classList.add('ic-error');
          } else if (row.result !== undefined && row.result !== null) {
            resultEl.textContent = row.result;
            resultEl.classList.remove('ic-error');
          } else {
            resultEl.textContent = '';
          }
        }

        // Bind row number
        const numEl = wrapper.querySelector('[data-ic-row-number]');
        if (numEl) {
          numEl.textContent = index + 1;
        }

        rowsContainer.appendChild(clone);
      });

      // Bind actions on new elements
      this.bindActions(rowsContainer);
    }

    // ===== SHARE MENU =====

    bindShareMenu() {
      const IC = window.Instacalc;

      // Update share badges
      document.querySelectorAll('[data-ic-share-badge]').forEach(el => {
        const type = IC.calcType;
        const badges = {
          'local': 'Local',
          'shared': 'Public',
          'public': 'Public',
          'private': 'Private',
          'wiki': 'Wiki'
        };
        el.textContent = badges[type] || 'Local';
        el.setAttribute('data-share-type', type);
      });

      // Update share URL display
      document.querySelectorAll('[data-ic-share-url]').forEach(el => {
        el.textContent = IC.getPublicURL() || '';
      });
    }

    // ===== ACTIONS =====

    bindActions(scope = document) {
      const IC = window.Instacalc;

      scope.querySelectorAll('[data-ic-action]').forEach(el => {
        // Skip if already bound
        if (this.boundElements.has(el)) return;
        this.boundElements.add(el);

        const action = el.getAttribute('data-ic-action');

        el.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          switch (action) {
            case 'new':
              IC.createCalc();
              break;

            case 'open': {
              const calcEl = el.closest('[data-id]');
              const id = calcEl?.getAttribute('data-id');
              if (id) IC.openCalc(id);
              break;
            }

            case 'delete': {
              const calcEl = el.closest('[data-id]');
              const id = calcEl?.getAttribute('data-id');
              if (id && confirm('Delete this calc?')) {
                IC.deleteCalc(id);
              }
              break;
            }

            case 'add-row':
              IC.addRow();
              break;

            case 'delete-row': {
              const rowEl = el.closest('[data-row-index]');
              const idx = rowEl?.getAttribute('data-row-index');
              if (idx !== null) {
                IC.deleteRow(parseInt(idx, 10));
              }
              break;
            }

            case 'share:public':
              IC.publishAs('public');
              break;

            case 'share:private':
              IC.publishAs('private');
              break;

            case 'share:wiki':
              IC.publishAs('wiki');
              break;

            case 'copy-link':
              IC.copyLink();
              break;

            default:
              console.warn('[instacalc-bridge] Unknown action:', action);
          }
        });
      });
    }

    // ===== DATA BINDINGS =====

    bindDataBindings() {
      const IC = window.Instacalc;
      const calc = IC.selectedCalc;

      // Calc count
      document.querySelectorAll('[data-ic-calc-count]').forEach(el => {
        el.textContent = IC.calcs.length;
      });

      // Current calc title (outside of list context)
      document.querySelectorAll('[data-ic-current-title]').forEach(el => {
        el.textContent = calc?.name || 'Untitled';
      });

      // Row count
      document.querySelectorAll('[data-ic-row-count]').forEach(el => {
        el.textContent = IC.rows.length;
      });
    }
  }

  // Initialize when DOM is ready
  const bridge = new InstacalcBridge();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bridge.init());
  } else {
    bridge.init();
  }

  // Expose for debugging
  window.InstacalcBridge = bridge;

  if (window.Debug) window.Debug.log('ui', 'instacalc-bridge loaded');
})();
